import {
  HomeSection,
  HomeSectionUpdatePayload,
} from "../../models/homeSections";
import { API_ENDPOINTS } from "../../utils/api-service";
import { api, tenantContextHeader } from "./api";

const homeSectionTag = (id: string) => ({ type: "homeSections" as const, id });
const homeSectionListTag = (tenantId: string) => ({
  type: "homeSections" as const,
  id: `LIST:${tenantId}`,
});

const unwrapApiData = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) {
    return response;
  }

  const record = response as Record<string, unknown>;
  if (record.success === true && record.data !== undefined) {
    return record.data;
  }

  return response;
};

const toInt = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

const toBoolean = (value: unknown): boolean => value === true;

const isHomeSectionType = (
  value: unknown
): value is HomeSection["sectionType"] =>
  value === "banners_carousel" ||
  value === "featured_events" ||
  value === "events_listing";

const mapHomeSection = (raw: unknown): HomeSection => {
  const row =
    typeof raw === "object" && raw !== null
      ? (raw as Record<string, unknown>)
      : {};

  return {
    id: String(row.id ?? ""),
    sectionType: isHomeSectionType(row.sectionType)
      ? row.sectionType
      : "banners_carousel",
    sortOrder: toInt(row.sortOrder),
    active: toBoolean(row.active),
    carouselIntervalSeconds: toInt(row.carouselIntervalSeconds, 5),
    carouselIsStatic: toBoolean(row.carouselIsStatic),
    eventsListingMaxItems: toInt(row.eventsListingMaxItems, 20),
    eventsListingDaysAhead: toInt(row.eventsListingDaysAhead, 30),
  };
};

const mapHomeSectionList = (response: unknown): HomeSection[] => {
  const data = unwrapApiData(response);
  if (Array.isArray(data)) {
    return data.map(mapHomeSection);
  }
  if (typeof data === "object" && data !== null) {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record.sections)) {
      return record.sections.map(mapHomeSection);
    }
  }
  return [];
};

export const homeSectionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getHomeSections: build.query<HomeSection[], { tenantId: string }>({
      query: ({ tenantId }) => ({
        url: API_ENDPOINTS.HOME_SECTIONS.BASE,
        headers: tenantContextHeader(tenantId),
      }),
      transformResponse: mapHomeSectionList,
      providesTags: (result, _error, { tenantId }) =>
        result
          ? [
              ...result.map((section) => homeSectionTag(section.id)),
              homeSectionListTag(tenantId),
            ]
          : [homeSectionListTag(tenantId)],
    }),

    updateHomeSection: build.mutation<HomeSection, HomeSectionUpdatePayload>({
      query: ({ id, tenantId, ...body }) => ({
        url: API_ENDPOINTS.HOME_SECTIONS.BY_ID(id),
        method: "PATCH",
        body,
        headers: tenantContextHeader(tenantId),
      }),
      transformResponse: (response: unknown) =>
        mapHomeSection(unwrapApiData(response)),
      async onQueryStarted({ tenantId }, { dispatch, queryFulfilled }) {
        try {
          const { data: savedSection } = await queryFulfilled;
          dispatch(homeSectionsApi.util.updateQueryData("getHomeSections", { tenantId }, (sections) => {
            const index = sections.findIndex((section) => section.id === savedSection.id);
            if (index !== -1) sections[index] = savedSection;
          }));
        } catch {
          return;
        }
      },
      invalidatesTags: (_result, error, { id, tenantId }) =>
        error ? [] : [homeSectionTag(id), homeSectionListTag(tenantId)],
    }),
  }),
});

export const {
  useGetHomeSectionsQuery,
  useUpdateHomeSectionMutation,
} = homeSectionsApi;
